import React, { useEffect, useState } from "react";
import axios from "axios";
import Button from "../components/Button";
import { MdExpandMore } from "react-icons/md";
import { AiOutlinePlus } from "react-icons/ai";
import FloatingLabelInput from "../components/FloatingLabelInput";
import { useNavigate } from "react-router-dom";
// MUI imports for table (desktop view)
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box } from "@mui/material";
// MUI imports for mobile card view
import { Card, CardContent, Typography, Grid, Checkbox } from "@mui/material";
import { AiOutlineSearch, AiOutlineReload } from "react-icons/ai"; // Import icons
import { Snackbar } from "@mui/material"; // Import Snackbar for notifications

// Base URL for the API
// const path = "http://103.251.218.254:3100";
const path = 'http://192.168.1.248:3103';
// Number of records to fetch per request
const limit = 10;

const UserDetailsPage_v1 = ({ loggedInUser, companyData }) => {
    // =========================
    // Main States
    // =========================
    const companyId = loggedInUser?.companyId;

    // searchData: Holds the values for the search filters to query users.
    const [searchData, setSearchData] = useState({
        userName: "",
        employeeName: "",
        mobileNumber: "",
        emailId: ""
    });

    // users: Array to store user records fetched from the backend.
    const [users, setUsers] = useState([]);

    // Pagination states: offset indicates the number of records already loaded,
    // and totalCount stores the total number of records available.
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // windowWidth: Tracks the browser window width to determine which layout to use.
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const [selectedIds, setSelectedIds] = useState([]); // Holds selected user IDs

    const [successMessage, setSuccessMessage] = useState(""); // Define state for success message

    // Navigation hook for programmatically navigating to other pages.
    const navigate = useNavigate();

    // =========================
    // Responsive Handling
    // =========================
    // Listen to window resize events to update the windowWidth state.
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // =========================
    // Data Fetching Function
    // =========================
    /**
     * fetchUsers
     * -----------
     * Asynchronously fetches user records from the API using search filters and pagination.
     *
     * @param {boolean} reset - If true, resets the offset to 0 and replaces the users state.
     *                          Otherwise, appends new records to the existing list.
     */
    const fetchUsers = async (reset = false, params = searchData) => {
        if (!companyId) return;
        try {
            const currentOffset = reset ? 0 : offset;
            const response = await axios.get(`${path}/api/userssearch`, { params: { companyId, offset: currentOffset, limit, ...params } });
            if (response.data.success) {
                const fetchedUsers = response.data.users || [];
                if (reset) {
                    setUsers(fetchedUsers);
                    setOffset(fetchedUsers.length);
                } else {
                    setUsers((prev) => [...prev, ...fetchedUsers]);
                    setOffset((prev) => prev + fetchedUsers.length);
                }
                setTotalCount(response.data.totalCount);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    // =========================
    // Initial Data Load & Search Debouncing
    // =========================
    // On component mount, load initial user data (resetting any previous records).
    useEffect(() => {
        fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // =========================
    // Event Handlers
    // =========================

    /**
     * FN_HandleSarchChanges
     * ------------------
     * Updates the searchData state when the user types into any search field.
     */
    const FN_HandleSarchChanges = (e) => {
        const { name, value } = e.target;
        setSearchData((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * FN_HandleReset
     * -----------
     * Resets all search filters to empty strings and reloads the initial user data.
     */
    const FN_HandleReset = () => {
        const resetData = {
            userName: "",
            employeeName: "",
            mobileNumber: "",
            emailId: ""
        };

        // Update the state (optional if you rely solely on resetData for this fetch)
        setSearchData(resetData);

        // Call fetchUsers with resetData so that the request uses the cleared fields immediately.
        fetchUsers(true, resetData);
    };

    /**
     * FN_HandleShowMore
     * --------------
     * Loads the next batch of user records (for pagination) and appends them to the current list.
    */
    const FN_HandleShowMore = async () => {
        await fetchUsers(false);
    };

    /**
     * handleUserClick
     * ---------------
     * Navigates to the UserMaster page in edit mode with the given userId.
     *
     * @param {string|number} userId - The ID of the user to edit.
     */
    const handleUserClick = (userId) => {
        console.log(userId);
        navigate("/main/v1/usermaster", { state: { mode: "edit", userId } });
    };

    /**
     * FN_HandleAddClick
     * --------------
     * Navigates to the UserMaster page in insert mode for creating a new user.
     */
    const FN_HandleAddClick = () => {
        navigate("/main/v1/usermaster", { state: { mode: "insert" } });
    };

    // Handles selection of an individual row
    const handleSelectUser = (userId) => {
        setSelectedIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    // Handles "Select All" checkbox functionality
    const handleSelectAll = (event, data) => {
        if (event.target.checked) {
            setSelectedIds(data.map((user) => user.userId));
        } else {
            setSelectedIds([]);
        }
    };

    // Delete selected users
    const handleDelete = async () => {
        if (selectedIds.length === 0) {
            alert("No users selected!");
            return;
        }

        try {
            // Step 1: Check if inquiries exist for the selected users
            const inquiryResponse = await axios.post(`${path}/api/check-inquiry`, { userIds: selectedIds });

            if (inquiryResponse.status === 400) {  // FIX: Use `response.status`, not `data.status`
                setSuccessMessage("User can't be deleted, inquiry exists");
                return;
            }

            // Step 2: Proceed with deletion if no inquiries exist
            const deleteResponse = await axios.post(`${path}/api/delete-users`, { userIds: selectedIds });

            if (deleteResponse.data.success) {
                setUsers((prev) => prev.filter((user) => !selectedIds.includes(user.userId))); // Remove deleted users from UI
                setSelectedIds([]); // Clear selection
                setSuccessMessage("Users deleted successfully!");
            } else {
                setSuccessMessage(deleteResponse.data.message || "Error deleting users!");
            }
        } catch (error) {
            console.log("Error deleting users:", error);

            // Improved error handling
            if (error.response) {
                setSuccessMessage(error.response.data.message || "Error deleting users!");
            } else {
                setSuccessMessage("Server error while deleting users!");
            }
        }
    };

    // =========================
    // Render Functions
    // =========================

    /**
     * HL_FN_RenderDesktopTable
     * ------------------
     * Renders user records in a table format for desktop screens using MUI Table components.
     *
     * @param {Array} data - Array of user records.
     */
    const HL_FN_RenderDesktopTable = (data) => (
        <TableContainer component={Paper} className="shadow-lg rounded-lg">
            <Table sx={{ minWidth: 650 }} size="small">
                <TableHead sx={{ backgroundColor: "#e5e7eb" }}>
                    <TableRow>
                        <TableCell align="center"> <Checkbox onChange={(e) => handleSelectAll(e, data)} checked={data.length > 0 && data.every((user) => selectedIds.includes(user.userId))} /> </TableCell>
                        <TableCell align="center">Sr No</TableCell>
                        <TableCell>User Name</TableCell>
                        <TableCell>Employee Name</TableCell>
                        <TableCell>Mobile No</TableCell>
                        <TableCell>Email ID</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((user, index) => (
                        <TableRow key={user.userId} hover onClick={() => handleUserClick(user.userId)}
                            sx={{
                                cursor: "pointer",
                                backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                            }}
                        >
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}> <Checkbox checked={selectedIds.includes(user.userId)} onChange={() => handleSelectUser(user.userId)} /> </TableCell>
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell sx={{ color: "blue", textDecoration: "underline" }} > {user.userName} </TableCell>
                            <TableCell>{user.employeeName}</TableCell>
                            <TableCell>{user.mobileNumber}</TableCell>
                            <TableCell>{user.emailId}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    /**
     * HL_FN_RenderMobileCard
     * -----------------
     * Renders user records as cards for mobile and medium devices using MUI Card components.
     *
     * @param {Array} data - Array of user records.
     */
    const HL_FN_RenderMobileCard = (data) => (
        <div className="flex flex-col gap-4 mt-3 w-full px-4">
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, mr: 1 }}>
                <Checkbox onChange={(e) => handleSelectAll(e, data)} checked={data.length > 0 && data.every((user) => selectedIds.includes(user.userId))} />
                <Typography variant="subtitle1">Select All</Typography>
            </Box>

            {data.map((user, index) => (
                <Card onClick={() => handleUserClick(user.userId)} // Keep click behavior for the card key={user.userId}
                    sx={{
                        cursor: "pointer",
                        boxShadow: 2,
                        "&:hover": { boxShadow: 4 },
                    }}
                >
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {/* Checkbox - Prevents click propagation */}
                            <Checkbox
                                checked={selectedIds.includes(user.userId)}
                                onClick={(e) => e.stopPropagation()} // Stop card click
                                onChange={() => handleSelectUser(user.userId)}
                            />
                            <Typography variant="h6" color="primary" gutterBottom>
                                {index + 1} - {user.userName}
                            </Typography>
                        </Box>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>Employee Name:</strong> {user.employeeName}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>Mobile No:</strong> {user.mobileNumber}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>Email ID:</strong> {user.emailId}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    /**
     * FN_RenderTable
     * -----------
     * Determines whether to render the desktop table or mobile card view based on window width.
     * For window widths below 1024px (small and medium devices), a card view is rendered.
     *
     * @param {Array} data - Array of user records.
     */
    const FN_RenderTable = (data) => {
        if (!data || data.length === 0) {
            return (
                <div className="flex justify-center items-center h-32 text-gray-600 font-semibold text-3xl">
                    No records found.
                </div>
            );
        }
        return windowWidth < 1024 ? HL_FN_RenderMobileCard(data) : HL_FN_RenderDesktopTable(data);
    };

    // =========================
    // JSX Returned by the Component
    // =========================
    return (
        <div className="container mx-auto p-6">
            {/* Page Heading */}
            <label className="flex justify-center underline font-semibold underline-offset-4 text-4xl mb-6">
                User Details
            </label>

            {/* Search Section (Always Visible) */}
            <div className="bg-gray-50 p-6 shadow rounded-lg border border-gray-200">
                {/* Search Section Label */}
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">Search Section</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <FloatingLabelInput label="User Name" name="userName" value={searchData.userName} onChange={FN_HandleSarchChanges} />
                    <FloatingLabelInput label="Employee Name" name="employeeName" value={searchData.employeeName} onChange={FN_HandleSarchChanges} />
                    <FloatingLabelInput label="Mobile Number" name="mobileNumber" value={searchData.mobileNumber} onChange={FN_HandleSarchChanges} />
                    <FloatingLabelInput label="Email ID" name="emailId" value={searchData.emailId} onChange={FN_HandleSarchChanges} />
                </div>
                <div className="flex justify-end mt-6 space-x-4">
                    {/* Search Button */}
                    <Button onClick={() => fetchUsers(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-md shadow-md transition duration-300" >
                        <AiOutlineSearch className="text-lg" /> Search
                    </Button>

                    {/* Reset Button */}
                    <Button onClick={FN_HandleReset} className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-md shadow-md transition duration-300" >
                        <AiOutlineReload className="text-lg" /> Reset
                    </Button>
                </div>
            </div>

            {/* Top Section: Add, Count, Show More, and Delete Button */}
            <div className="flex flex-row md:flex-row justify-evenly md:justify-between items-center mt-6 pt-4 text-sm">
                {/* Left: Add Button */}
                <button onClick={FN_HandleAddClick} className="flex items-center bg-green-500 text-white w-14 h-10 md:w-fit md:h-10 md:px-4 px-1 rounded-md hover:bg-green-600 mb-4 md:mb-0" >
                    <AiOutlinePlus size={18} className="mr-1" /> Add
                </button>

                {/* Center: Record Count */}
                <div className="mb-4 md:mb-0">Count: {users.length} / {totalCount}</div>

                <div className="flex items-center gap-4">
                    {/* Show More Button (if more records exist) */}
                    {users.length < totalCount && (
                        <button onClick={FN_HandleShowMore} className="flex items-center h-10 w-24 bg-blue-500 text-white px-4 rounded-md hover:bg-blue-600" >
                            Show More
                        </button>
                    )}

                    {/* Delete Button */}
                    <button onClick={handleDelete} className="flex items-center h-10 w-24 bg-red-500 text-white px-4 rounded-md hover:bg-red-600" >
                        Delete
                    </button>
                </div>
            </div>

            {/* Data Table or Card View (Responsive) */}
            <div className=" mt-6">
                {FN_RenderTable(users)}
            </div>
            {/* Snackbar to display messages */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={null}  // Stays open until manually closed
                onClose={() => setSuccessMessage("")}
                message={successMessage}
                action={
                    <button onClick={() => setSuccessMessage("")} style={{ color: "white" }}>
                        Close
                    </button>
                }
            />
        </div>
    );
};

export default UserDetailsPage_v1;